import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import TVSeriesCard from '@/components/feature/TVSeriesCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { tvSeries } from '@/mocks/tvSeries';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const series = useMemo(() => tvSeries.find((s) => s.id === Number(id)), [id]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!series) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-14 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-file-unknow-line text-3xl"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">剧集未找到</h1>
            <p className="text-foreground-500 mb-4">抱歉，您访问的剧集不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedSeries = tvSeries
    .filter((s) => s.id !== series.id && (s.categorySlug === series.categorySlug || s.tags.some((t) => series.tags.includes(t))))
    .slice(0, 6);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${series.title} - 天美影视高清全集在线观看`,
    description: series.description,
    datePublished: `${series.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: '天美影视',
    },
    publisher: {
      '@type': 'Organization',
      name: '天美影视',
      url: 'https://www.tianmeitv.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.tianmeitv.com/detail/${series.id}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${series.title}全集在线观看 - 天美影视高清免费追剧`}
        description={`天美影视为您提供${series.title}全集高清免费在线观看，${series.description}导演${series.director}，主演${series.cast.slice(0, 3).join('、')}等，共${series.episodes}集${series.status === '已完结' ? '已完结' : '更新中'}。`}
        keywords={`${series.title},${series.title}在线观看,${series.title}全集,天美影视,${series.category}电视剧,高清电视剧,免费在线观看`}
        canonicalUrl={`https://www.tianmeitv.com/detail/${series.id}`}
        ogType="article"
        jsonLd={[articleSchema]}
      />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: series.category, path: `/category/${series.categorySlug}` },
                { label: series.title },
              ]}
            />
          </div>

          {/* Detail Header */}
          <article className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
            <div className="w-full md:w-64 flex-shrink-0">
              <div className="aspect-[3/4] rounded-xl overflow-hidden">
                <img
                  src={series.cover}
                  alt={`${series.title} 天美影视海报`}
                  title={`${series.title} - 天美影视高清在线观看`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                {series.title}
              </h1>
              <div className="flex items-center gap-3 flex-wrap mb-3">
                <span className="flex items-center gap-1 text-base font-semibold text-accent-600">
                  <i className="ri-star-fill text-lg"></i>
                  {series.rating}
                </span>
                <span className="text-sm text-foreground-500">{series.year}</span>
                <span className="text-sm text-foreground-500">{series.episodes}集</span>
                <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                  series.status === '更新中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-background-200 text-foreground-500'
                }`}>
                  {series.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-4">
                {series.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="space-y-2 mb-4">
                <p className="text-sm text-foreground-700">
                  <strong className="text-foreground-900">导演：</strong>{series.director}
                </p>
                <p className="text-sm text-foreground-700">
                  <strong className="text-foreground-900">主演：</strong>{series.cast.join('、')}
                </p>
                <p className="text-sm text-foreground-700">
                  <strong className="text-foreground-900">类型：</strong>
                  <Link to={`/category/${series.categorySlug}`} className="text-primary-500 hover:text-primary-600 transition-colors">
                    {series.category}
                  </Link>
                </p>
                <p className="text-sm text-foreground-700">
                  <strong className="text-foreground-900">状态：</strong>{series.status}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </button>
                <button className="inline-flex items-center gap-1.5 bg-background-200 hover:bg-background-300 text-foreground-700 px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </article>

          {/* Plot Summary */}
          <section className="mb-10 bg-background-50 rounded-xl p-5 md:p-6 border border-background-200">
            <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情介绍</h2>
            <p className="text-sm text-foreground-700 leading-relaxed">{series.longDescription}</p>
          </section>

          {/* Related Series */}
          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-900">
                相关推荐
              </h2>
              <Link
                to={`/category/${series.categorySlug}`}
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                更多{series.category} <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {relatedSeries.map((s) => (
                <TVSeriesCard key={s.id} series={s} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}